import React, { useState, useEffect } from 'react';
import { fetchSalaries } from '../api';
import Loader from '../components/Loader';


function toLPA(amount) {
  if (!amount) return null;
  const lpa = amount / 100000;
  if (lpa < 1 || lpa > 200) return null;
  return lpa;
}

function formatRange(min, max) {
  const lo = toLPA(min);
  const hi = toLPA(max);
  if (!lo && !hi) return '—';
  if (!lo || !hi) {
    const v = lo || hi;
    return `₹${v.toFixed(1)} LPA`;
  }
  const lower = Math.min(lo, hi);
  const upper = Math.max(lo, hi);
  if (lower === upper) return `₹${lower.toFixed(1)} LPA`;
  return `₹${lower.toFixed(1)}L – ₹${upper.toFixed(1)} LPA`;
}


export default function SalariesPage({ setView, setSelectedCompany, setSelectedRole, onLocationNavigate }) {
  const [salaries, setSalaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchRole, setSearchRole] = useState('');

  useEffect(() => {
    async function loadSalaries() {
      setLoading(true);
      try {
        const data = await fetchSalaries({ limit: 100 });
        setSalaries(data.salaries || []);
      } catch (err) {
        console.error('Failed to load salaries:', err);
      } finally {
        setLoading(false);
      }
    }
    loadSalaries();
  }, []);

  const filtered = salaries
    .filter(s => !searchRole || s.role?.toLowerCase().includes(searchRole.toLowerCase()))
    .sort((a, b) => (b.avg || 0) - (a.avg || 0));

  return (
    <div style={{ maxWidth: '100%', margin: '0 auto', padding: '8px 0', minHeight: 'calc(100vh - 60px)' }}>
      <div style={{ position: 'sticky', top: 0, zIndex: 10, background: 'var(--bg)', padding: '2px 0 6px', marginBottom: 4 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <h1 style={{ fontFamily: 'var(--display)', fontSize: 15, fontWeight: 900, margin: 0, color: 'var(--text)', whiteSpace: 'nowrap' }}>
            Salary Benchmarks
          </h1>
          <div style={{ position: 'relative', flex: 1, maxWidth: 280 }}>
            <svg style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text3)', pointerEvents: 'none' }}
              width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
            </svg>
            <input
              value={searchRole}
              onChange={e => setSearchRole(e.target.value)}
              placeholder="Search role…"
              style={{
                width: '100%', padding: '6px 10px 6px 28px', borderRadius: 8,
                border: '1px solid var(--border)', background: 'var(--surface2)',
                fontSize: 12, color: 'var(--text)', outline: 'none',
                fontFamily: 'var(--sans)', transition: 'border-color 0.2s',
              }}
              onFocus={e => e.target.style.borderColor = 'var(--blue)'}
              onBlur={e => e.target.style.borderColor = 'var(--border)'}
            />
            {searchRole && (
              <button onClick={() => setSearchRole('')} style={{
                position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--text3)', fontSize: 14, lineHeight: 1, padding: 2,
              }}>×</button>
            )}
          </div>
        </div>
      </div>

      {loading ? (
        <Loader inline message="Analyzing benchmark data..." />
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text3)' }}>
          {searchRole ? 'No matching roles found.' : 'No salary data reported yet.'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', borderTop: '1px solid var(--border)' }}>
          {/* Column Header */}
          <div className="salary-grid-header" style={{
            display: 'grid', gridTemplateColumns: '1fr 100px 140px 32px',
            padding: '6px 4px', gap: 16,
            fontSize: 10, fontWeight: 700, color: 'var(--text3)',
            textTransform: 'uppercase', letterSpacing: 0.5,
            borderBottom: '1px solid var(--border)',
          }}>
            <span>Job Role</span>
            <span className="salary-header-kahanis" style={{ textAlign: 'center' }}>Kahani's</span>
            <span style={{ textAlign: 'right' }}>CTC Range (LPA)</span>
            <span className="salary-header-chevron" />
          </div>

          {filtered.map((s, i) => (
            <div
              key={i}
              className="salary-grid-row"
              onClick={() => {
                if (setSelectedCompany && setView) {
                  setSelectedCompany(s.company, 'All');
                  if (setSelectedRole) setSelectedRole(s.role);
                  if (onLocationNavigate) onLocationNavigate('');
                  setView('company-reviews');
                }
              }}
              style={{
                background: 'transparent',
                borderBottom: '1px solid var(--border)',
                padding: '10px 4px',
                display: 'grid', gridTemplateColumns: '1fr 100px 140px 32px',
                alignItems: 'center', gap: 16,
                cursor: 'pointer', transition: 'background 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--surface2)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
            >
              {/* Role Info */}
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--text)', marginBottom: 2 }}>{s.role}</div>
                {/* Count badge shown inline below role name — mobile only */}
                <div className="salary-mobile-count" style={{ display: 'none', marginTop: 4 }}>
                  <span style={{
                    fontFamily: 'var(--sans)', fontSize: 11, fontWeight: 700,
                    color: 'var(--blue)', background: 'rgba(14,165,233,0.1)',
                    padding: '2px 8px', borderRadius: 6,
                  }}>
                    {s.count} kahani{s.count !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>

              {/* Report Count — desktop column */}
              <div className="salary-grid-count" style={{ textAlign: 'center' }}>
                <div style={{
                  fontFamily: 'var(--sans)', fontSize: 13, fontWeight: 800,
                  color: 'var(--blue)', background: 'rgba(14,165,233,0.08)',
                  padding: '4px 10px', borderRadius: 8, display: 'inline-block'
                }}>
                  {s.count}
                </div>
              </div>

              {/* CTC Range */}
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontFamily: 'var(--sans)', fontWeight: 800, fontSize: 13, color: 'var(--text)' }}>
                  {formatRange(s.min, s.max)}
                </div>
              </div>

              {/* Chevron */}
              <svg className="salary-grid-chevron" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--text3)" strokeWidth="2.5">
                <path d="m9 18 6-6-6-6" />
              </svg>
            </div>
          ))}
        </div>
      )}

      <div style={{ marginTop: 24, background: 'rgba(14,165,233,0.06)', border: '1px solid rgba(14,165,233,0.2)', borderRadius: 12, padding: '16px 20px' }}>
        <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.6 }}>
          💡 <strong>Note:</strong> Salaries shown are approximate TC (Total Compensation) ranges reported by our community. Data is updated based on verified submissions.
        </div>
      </div>
    </div>
  );
}
